import React, { useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import type { ChipOwnProps } from "@mui/material/Chip";

interface KanbanColumnShellProps {
  status: string;
  chipColor?: ChipOwnProps["color"];
  count: number;
  width?: number;
  onDrop: () => void;
  headerExtra?: React.ReactNode;
  emptyText?: string;
  children: React.ReactNode;
}

function KanbanColumnShell({
  status,
  chipColor = "default",
  count,
  width = 240,
  onDrop,
  headerExtra,
  emptyText = "Empty",
  children,
}: KanbanColumnShellProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <Box sx={{ width, flexShrink: 0, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ mb: 1, px: 0.5, pb: 0.75, borderBottom: "1px solid #F1F5F9" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={headerExtra ? 0.25 : 0}
        >
          <Chip
            label={status}
            color={chipColor}
            size="small"
            sx={{
              fontSize: "0.68rem",
              height: 21,
              borderRadius: "4px",
              maxWidth: width - 50,
              "& .MuiChip-label": {
                px: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
          <Box
            sx={{
              minWidth: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: count > 0 ? "#0F172A" : "#E2E8F0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.5,
            }}
          >
            <Typography
              sx={{
                color: count > 0 ? "white" : "#94A3B8",
                fontSize: "0.6rem",
                fontWeight: 700,
              }}
            >
              {count}
            </Typography>
          </Box>
        </Stack>
        {headerExtra}
      </Box>

      {/* Drop zone */}
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={() => {
          setIsDragOver(false);
          onDrop();
        }}
        sx={{
          flex: 1,
          borderRadius: "8px",
          border: `2px dashed ${isDragOver ? "#0EA5E9" : "transparent"}`,
          bgcolor: isDragOver ? "#EFF6FF" : "#F8FAFC",
          p: 0.75,
          transition: "border-color 0.15s ease, background-color 0.15s ease",
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          minHeight: 80,
          maxHeight: "calc(100vh - 240px)",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 3 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 999 },
        }}
      >
        {children}
        {count === 0 && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ textAlign: "center", mt: 2, display: "block", userSelect: "none" }}
          >
            {isDragOver ? "Drop here" : emptyText}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default KanbanColumnShell;
